import { defineStore } from "pinia";
import api from "@/_helpers/api.ts";
import { axiosInstance } from "@/plugins/https.ts";
import { StoreModule } from "@/constants/store.ts";
import { useSnackbarStore } from "@/_store/snackbar.module.ts";
import { i18n } from "@/plugins/i18n.ts";
import { ConditionalForwardingAction } from "@/constants/network.ts";

export interface BaseConditionalForwardingItem {
  dnsServer: string;
  localDomainName: string;
}

export interface ConditionalForwardingItem extends BaseConditionalForwardingItem {
  id: string;
  subnet: string;
}

export interface ConditionalForwardingState {
  items: Array<ConditionalForwardingItem>;
  loading: boolean;
}

const defaultConditionalForwardingState: ConditionalForwardingState = {
  items: [],
  loading: false,
};

export const useConditionalForwardingStore = defineStore(StoreModule.CONDITIONAL_FORWARDING, {
  state: (): ConditionalForwardingState => ({ ...defaultConditionalForwardingState }),
  actions: {
    async getItems() {
      const requestConfig = {
        ...api.conditionalForwarding(),
        method: "GET",
      };

      try {
        this.loading = true;
        const { data } = await axiosInstance.request(requestConfig);
        this.items = data ?? [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async addItem(data: Omit<ConditionalForwardingItem, "id">) {
      const requestConfig = {
        ...api.conditionalForwarding(),
        method: "POST",
        data,
      };

      try {
        await axiosInstance.request(requestConfig);
        await this.getItems();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(
            `snackbar.messages.secureWebGateway.conditionalForwarding.${ConditionalForwardingAction.ADD}`
          )
        );
      } catch (e) {
        console.error(e);
      }
    },
    async updateItem(id: string, data: Omit<ConditionalForwardingItem, "id">) {
      const requestConfig = {
        ...api.conditionalForwardingById(id),
        method: "PUT",
        data,
      };

      try {
        await axiosInstance.request(requestConfig);
        await this.getItems();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(
            `snackbar.messages.secureWebGateway.conditionalForwarding.${ConditionalForwardingAction.EDIT}`
          )
        );
      } catch (e) {
        console.error(e);
      }
    },
    async deleteItem(id: string) {
      const requestConfig = {
        ...api.conditionalForwardingById(id),
        method: "DELETE",
      };

      try {
        await axiosInstance.request(requestConfig);
        await this.getItems();
        useSnackbarStore().addGenericSuccess(
          i18n.global.t(
            `snackbar.messages.secureWebGateway.conditionalForwarding.${ConditionalForwardingAction.REMOVE}`
          )
        );
      } catch (e) {
        console.error(e);
      }
    },
  },
});
